<script setup>

import { defineProps, defineEmits, computed, watch, ref} from "vue";
import BarraNavegacao from "./BarraNavegacao.vue";
import DadoConteudo from "./DadoConteudo.vue";
import UtilObject from "@/utils/UtilObject";
import ERP from "@apiV2/ERP.mjs";
import View from "@/api/v2/View.mjs";

//==================================================================//
//========================= Props & Emits ==========================//
//==================================================================//

const props = defineProps([
    "usuarioStore",
    "colaboradorStore",
    "controlesDesativados",
    "gerandoPagamentos",
    "dadoOriginal",
]);

const emits = defineEmits([
    "update:alteraControlesDesativados",
    "update:alteraGerandoPagamentos",
]);

//==================================================================//
//============================= Dado ===============================//
//==================================================================//

const dado = ref({});

//==================================================================//
//=================== Notas Fiscais Recebidas ======================//
//==================================================================//

const notasFiscaisRecebidas = ref([]);

//==================================================================//
//============== Loaded Notas Fiscais Recebidas ====================//
//==================================================================//

const loadedNotasFiscaisRecebidas = ref(false);

//==================================================================//
//======================== Adiantamentos ===========================//
//==================================================================//

const adiantamentos = ref([]);

//==================================================================//
//=================== Loaded Adiantamentos =========================//
//==================================================================//

const loadedAdiantamentos = ref(false);

//==================================================================//
//================= Altera Controles Desativados ===================//
//==================================================================//

const alteraControlesDesativados = (update)=>{

    emits('update:alteraControlesDesativados',update);

};

//==================================================================//
//================== Altera Gerando Pagamentos =====================//
//==================================================================//

const alteraGerandoPagamentos = (update = {}) =>{

    // Sempre fecha o Modal pois é o unico maneira que o Modal interage com esse método.
    update.modal = false;

    // Reseta o Loaded de Notas Fiscais Recebidas.
    alteraLoadedNotasFiscaisRecebidas(false);

    emits('update:alteraGerandoPagamentos',update);

};

//==================================================================//
//========================= Mostra Modal ===========================//
//==================================================================//

const mostraModal = computed(()=>{

    // Se Gerando Pagamentos for False.
    if(props.gerandoPagamentos == false){
        return false;
    }

    // Se Notas Fiscais Recebidas não foram carregadas
    if(loadedNotasFiscaisRecebidas.value == false){
        return false;
    }

    return true;

});

//==================================================================//
//===================== Altera Dado de Compra ======================//
//==================================================================//

const alteraDado = (update = {})=>{

    dado.value = UtilObject.merge(dado.value,update);

};

//==================================================================//
//======================= Altera Contas a Pagar ====================//
//==================================================================//

const alteraContasAPagar = (update = {})=>{

    let contasAPagar = dado?.value?.contasAPagar?.map((contaAPagar)=>{

        return UtilObject.merge(contaAPagar,update);


    });

    let selecionado = contasAPagar?.every(({selecionado})=> selecionado == true);

    alteraDado({
        selecionado: selecionado,
        contasAPagar: contasAPagar
    });

};

//==================================================================//
//=================== Altera Conta a Pagar =========================//
//==================================================================//

const alteraContaAPagar = (update)=>{

    let contasAPagar = dado?.value?.contasAPagar?.map((contaAPagar)=>{

        if(contaAPagar.id != update.id){
            return contaAPagar;
        }

        return UtilObject.merge(contaAPagar,update);

    });

    let selecionado = contasAPagar?.every(({selecionado})=> selecionado == true);

    alteraDado({
        selecionado: selecionado,
        contasAPagar: contasAPagar
    });

};

//==================================================================//
//================== Altera Pagamento Novo =========================//
//==================================================================//

const alteraPagamentoNovo = (update)=>{

    let contasAPagar = dado?.value?.contasAPagar?.map((contaAPagar)=>{

        if(contaAPagar.id != update?.contaAPagar?.id){
            return contaAPagar;
        }

        // Alteração em Pagamento Novo
        contaAPagar.pagamentoNovo = UtilObject.merge(contaAPagar.pagamentoNovo, update.pagamentoNovo);

        // Alteração em Conta a Pagar
        let contaAPagarValorParaReservar = contaAPagar?.valorParaReservar || 0;
        let pagamentoNovoValorReservado = contaAPagar?.pagamentoNovo?.valorReservado || 0;

        contaAPagar.valorParaReservarRestante = contaAPagarValorParaReservar - pagamentoNovoValorReservado;

        return contaAPagar;

    });

    let selecionado = contasAPagar?.every(({selecionado})=> selecionado == true);

    alteraDado({
        selecionado: selecionado,
        contasAPagar: contasAPagar
    });

    recalculaNotasFiscaisRebidas();
    recalculaAdiantamentos();

};

//===================================================================//
//================== Recalcula Nota Fiscal Emitida ==================//
//===================================================================//

const recalculaNotasFiscaisRebidas = ()=>{

    for (const notaFiscalRecebida of notasFiscaisRecebidas.value){

        // Pega a Soma do Valor Reservado dos Pagamentos para atualizar o quanto foi Reservado a essa Nota Fiscal Recebida.
        const pagamentosValorReservado = dado?.value?.contasAPagar?.reduce((acc,{pagamentoNovo})=>{

            if(pagamentoNovo?.vinculoTipo != "Nota Fiscal Recebida"){
                return acc;
            }

            acc += pagamentoNovo?.valorReservado;

            return acc;

        },0);

        // Recalcula a Nota Fiscal Recebida e Altera ela.
        notaFiscalRecebida.valorParaReservarRestante = notaFiscalRecebida?.valorParaReservar - pagamentosValorReservado; 

    }
    
};

//===================================================================//
//=================== Recalcula Nota Fiscal Emitida =================//
//===================================================================//

const recalculaAdiantamentos = ()=>{

    for (const adiantamento of adiantamentos.value){

        // Pega a Soma do Valor Vinculado dos Pagamentos para atualizar o quanto foi Vinculado a esse Adiantamento.
        const pagamentosValorVinculado = dado?.value?.contasAPagar?.reduce((acc,{pagamentoNovo})=>{

            if(pagamentoNovo?.vinculoTipo != "Adiantamento"){
                return acc;
            }

            acc += pagamentoNovo?.valorReservado;

            return acc;

        },0);

        // Recalcula a Nota Fiscal Recebida e Altera ela.
        adiantamento.valorParaVincularRestante = adiantamento?.valorParaVincular - pagamentosValorVinculado; 

    }

};

//===================================================================//
//================ Altera Notas Fiscais Recebidas ===================//
//===================================================================//

const alteraNotasFiscaisRecebidas = (update = []) =>{

    notasFiscaisRecebidas.value = update;

};

//===================================================================//
//============= Altera Loaded Notas Fiscais Recebidas ===============//
//===================================================================//

const alteraLoadedNotasFiscaisRecebidas = (update = false) =>{

    loadedNotasFiscaisRecebidas.value = update;

};

//===================================================================//
//====================== Altera Adiantamentos =======================//
//===================================================================//

const alteraAdiantamentos = (update = []) =>{

    adiantamentos.value = update;

};

//===================================================================//
//============= Altera Loaded Notas Fiscais Recebidas ===============//
//===================================================================//

const alteraLoadedAdiantamentos = (update = false) =>{

    loadedAdiantamentos.value = update;

};

//===================================================================//
//========================== Prepara Dado ===========================//
//=================================================-=================//

const preparaDado = (dado = {})=>{

    //===================================================================//
    //===================== FILTRA PARCELAS VALIDAS =====================//
    //===================================================================//
    // Considera todas as Contas a Pagar que estão Liberadas
    // Considera todas as Contas a Pagar que tem Valor para Reservar.

    let parcelasValidas = dado?.parcelas?.filter(
        parcela =>
            parcela?.contaAPagar.status == "Liberada" &&
            parcela?.contaAPagar?.valorParaReservar > 0
    );

    //===================================================================//
    //======================== CONTAS A PAGAR ===========================//
    //===================================================================//

    let contasAPagar = parcelasValidas?.reduce((acc,parcela,index)=>{

        //===================================================================//
        //====================== AUX CONTA A PAGAR ==========================//
        //===================================================================//

        let contaAPagar = parcela?.contaAPagar;

        //===================================================================//
        //==============-======== TIPO DE VINCULO =-=========================//
        //===================================================================//

        // Determina o tipo padrão de vínculo para um novo pagamento desta Conta a Pagar:
        // - Se for Adiantamento, não pode ser vinculada (Nenhum)
        // - Se o tipo do dado for 'Ordem de Pagamento', também é 'Nenhum'
        // - Caso contrário, é 'Nota Fiscal Recebida'

        let vinculoTipo;

        if(dado.tipo === "Ordem de Pagamento"){
            
            vinculoTipo = "Nenhum";

        }else if(contaAPagar?.pagamentoTipo === "Adiantamento"){

            vinculoTipo = "Nenhum";

        }else {

            vinculoTipo = "Nota Fiscal Recebida";

        }

        //===================================================================//
        //============================= PUSH ================================//
        //===================================================================//
        
        acc.push({
            id: contaAPagar?.id || "",
            aberto: false,
            selecionado: false,
            codigo: contaAPagar?.codigo || "",
            parcelaCodigo: parcela?.codigo || "",
            status: contaAPagar?.status || "",
            forma: contaAPagar?.pagamentoForma || "",
            tipo: contaAPagar?.pagamentoTipo || "",
            evento: contaAPagar?.pagamentoEvento || "",
            descricao: contaAPagar?.descricao || "",
            dataVencimento: contaAPagar?.dataVencimentoFormatada || "",
            valorOriginal: contaAPagar?.valorOriginal || 0,
            valorParaReservar: contaAPagar?.valorParaReservar || 0,
            valorParaReservarRestante: contaAPagar?.valorParaReservar || 0,
            valorDesconto: contaAPagar?.valorDesconto || 0,
            valorPagamento: contaAPagar?.valorPagamento || 0,
            valorReservado: contaAPagar?.valorReservado || 0,
            valorParaPagar: contaAPagar?.valorParaPagar || 0,
            valorPago: contaAPagar?.valorPago || 0,
            valorParaVincular: contaAPagar?.valorParaVincular || 0,
            valorVinculado: contaAPagar?.valorVinculado || 0,
            pagamentoNovo:{
                id: `${contaAPagar?.codigo}-${index}`,
                vinculoTipo: vinculoTipo || "",
                adiantamento: {},
                notaFiscalRecebida: {},
                valorDesconto: 0,
                valorPagamento: 0,
                valorReservado: 0,
            }
        });

        return acc;

    },[]);

    dado = {
        id: dado?.id,
        selecionado: false,
        tipo: dado?.tipo,
        codigo: dado?.codigo,
        contasAPagar: contasAPagar || []
    }

    alteraDado(dado);

};

//===================================================================//
//================ Prepara Notas Fiscais Recebidas ==================//
//===================================================================//

const preparaNotasFiscaisRecebidas = async (dado={})=>{

    alteraLoadedNotasFiscaisRecebidas(false);

    let notasFiscaisRecebidasResponse = await ERP.getManyWithView(View.CONTAS_A_PAGAR_GERAR_PAGAMENTOS_NOTAS_FISCAIS_RECEBIDAS,[
        {
            name: "pedidoDeCompra",
            dataType: "String",
            value: dado?.id
        },
    ]);

    alteraNotasFiscaisRecebidas(notasFiscaisRecebidasResponse.data);
    alteraLoadedNotasFiscaisRecebidas(true);

};

//===================================================================//
//================ Prepara Notas Fiscais Recebidas ==================//
//===================================================================//

const preparaAdiantamentos = async (dado={})=>{

    alteraLoadedAdiantamentos(false);

    let adiantamentos = dado?.parcelas?.reduce((acc,parcela)=>{

        let contaAPagar = parcela?.contaAPagar;
        
        // Desconsidera Contas a Pagar que não são Adiantamento.
        // Desconsidera Contas a Pagar que não tem Valor para Vincular
        if(
            contaAPagar?.pagamentoTipo != "Adiantamento" ||
            contaAPagar?.valorParaVincular <= 0
        ){
            return acc;
        }

        let adiantamento = {
            id: contaAPagar?.id || "",
            tipo: "Adiantamento",
            codigo: contaAPagar?.codigo || "",
            valorOriginal: contaAPagar?.valorOriginal || 0,
            valorParaVincular: contaAPagar?.valorParaVincular || 0, 
            valorParaVincularRestante: contaAPagar?.valorParaVincular || 0,
            valorVinculado: contaAPagar?.valorVinculado ||0
        };

        acc.push(adiantamento);

        return acc;

    },[]);

    alteraAdiantamentos(adiantamentos);
    alteraLoadedAdiantamentos(true);

};

//==================================================================//
//======================== Prepara Modal ===========================//
//==================================================================//

const preparaModal = async ()=>{

    preparaDado(UtilObject.clone(props?.dadoOriginal));
    await preparaNotasFiscaisRecebidas(props?.dadoOriginal);
    preparaAdiantamentos(props?.dadoOriginal);

}

//====================================================================//
//=== Prepara Modal se Dados Originais Carregados ou Recarregados ====//
//=================== Watch => gerandoPagamentos =====================//
//====================================================================//

watch(()=> props?.gerandoPagamentos, async () => {

    if(props?.gerandoPagamentos == true){
        await preparaModal();
    }

},{ immediate: true });

</script>

<template>

    <main>

        <v-dialog
            content-class="c-dialog"
            v-model="mostraModal"
            fullscreen
            transition="dialog-bottom-transition"
            persistent
        >

            <v-card class="c-modal">

                <BarraNavegacao
                    :usuarioStore="props.usuarioStore"
                    :colaboradorStore="props.colaboradorStore"
                    :controlesDesativados="controlesDesativados"
                    :dado="dado"
                    @update:alteraControlesDesativados="alteraControlesDesativados"
                    @update:alteraGerandoPagamentos="alteraGerandoPagamentos"
                ></BarraNavegacao>

                <DadoConteudo
                    :controlesDesativados="props.controlesDesativados"
                    :dado="dado"
                    :notasFiscaisRecebidas="notasFiscaisRecebidas"
                    :adiantamentos="adiantamentos"
                    @update:alteraContasAPagar="alteraContasAPagar"
                    @update:alteraContaAPagar="alteraContaAPagar"
                    @update:alteraPagamentoNovo="alteraPagamentoNovo"
                ></DadoConteudo>
            
            </v-card>

        </v-dialog>

    </main>

</template>

<style scoped>

.v-dialog__content >>> .v-dialog{
    background-color: white;
}

</style>

